import * as React from 'react';
import { useEffect, useState } from 'react';
import styled from '@mui/system/styled';
import AgGridReact from 'ui/AgGridReact/AgGridReact';
import Typography from '@mui/material/Typography';
import { ColDef } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';
import { useGetBudgetProposals } from 'routes/Home/hooks/useGetBudgetProposals';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import { BudgetPhases } from 'business/constants';
import Flex from 'ui/Flex/Flex';
import { Link } from 'react-router-dom';
import { useHasDafAccess } from 'hooks/user';
import { BudgetProposalRow, BudgetStateColumn } from './types';
import BudgetStatesCellRenderer from './BudgetStatesCellRenderer';
import { getBpSectionLabel } from 'routes/BudgetModule/helpers/budgetProposalHelpers';

const StyBudgetStatesContainer = styled('div', {
    name: 'StyBudgetStatesContainer',
})(() => ({
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '16px',
    boxShadow: '-4px 8px 24px 0px rgba(0, 0, 0, 0.06)',
}));

const StyButton = styled(Link, { name: 'StyButton' })(({ theme }) => ({
    padding: '4px 8px',
    border: 'solid 1px',
    lineHeight: '1.75',
    borderRadius: '4px',
    fontWeight: 600,
    transition: 'background-color 0.3s ease',
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
}));

const StyTableContainer = styled('div', { name: 'StyTableContainer' })(() => ({
    marginTop: '8px',
    marginLeft: '-14px',
    minHeight: '26px',
    height: '100%',
    '& div.ag-header': { border: 'unset' },
    '& div.ag-row': { border: 'unset' },
    '& div.ag-paging-panel': { border: 'unset' },
}));

const gridOptions = {
    suppressRowClickSelection: true, // Disable row selection on row click
    suppressCellSelection: true, // Disable cell selection
    getContextMenuItems: () => [], // Remove context menu on rightClick
    autoWidth: true,
    autoHeight: true,
};

const BudgetStates = (): JSX.Element => {
    const { data, isLoading } = useGetBudgetProposals();
    const [rowData, setRowData] = useState<BudgetProposalRow[]>([]);
    const { t } = useTranslation();
    const hasAccessDaf = useHasDafAccess();

    const columnDefs: ColDef[] = [
        {
            headerName: t('home.budgetStates.budget'),
            field: 'budget',
            cellRendererParams: { colId: BudgetStateColumn.BUDGET },
            width: 1000,
            cellRenderer: 'budgetStatesCellRenderer',
        },
        {
            headerName: t('home.budgetStates.exercise'),
            field: 'exercise',
            cellRendererParams: { colId: BudgetStateColumn.EXERCISE },
            width: 150,
            cellRenderer: 'budgetStatesCellRenderer',
        },
        {
            headerName: t('home.budgetStates.phase'),
            field: 'phase',
            cellRendererParams: { colId: BudgetStateColumn.PHASE },
            width: 300,
            cellRenderer: 'budgetStatesCellRenderer',
        },
    ];

    const getBudgetProposalTitle = (bp: BudgetProposalT): string => {
        if (!bp.section) return bp.budgetName;
        const sectionLabel = getBpSectionLabel(bp.section, t);

        return `${bp.budgetName} - ${sectionLabel}`;
    };

    const getRowDataFromBudgetProposals = (
        budgetProposals: BudgetProposalT[],
    ): BudgetProposalRow[] =>
        budgetProposals.map((bp) => ({
            budget: getBudgetProposalTitle(bp),
            exercise: bp.exercice,
            phase: t(`budgetProposal.${bp.phase}`),
            redirectTo: `/budget_proposal/${bp.id}`,
        }));

    const filterAndSortBudgetProposals = (
        budgetProposals: BudgetProposalT[],
    ): BudgetProposalT[] =>
        budgetProposals
            .filter((bp) => bp.phase !== BudgetPhases.ARCHIVED)
            .sort((a, b) => {
                if (a.id <= b.id) return 1;
                return -1;
            });

    useEffect(() => {
        if (!isLoading && data) {
            const budgetProposals = filterAndSortBudgetProposals(data);
            setRowData(getRowDataFromBudgetProposals(budgetProposals));
        }
    }, [data, isLoading]);

    return (
        <StyBudgetStatesContainer>
            <Flex align="center" justify="space-between">
                <Typography variant="h5">
                    {t('home.budgetStates.title')}
                </Typography>
                {hasAccessDaf && (
                    <StyButton to="/budget_overview/finance_ratios">
                        {t('home.budgetStates.dafView')}
                    </StyButton>
                )}
            </Flex>
            <StyTableContainer className="ag-theme-material" sx={{ mt: 2 }}>
                <AgGridReact
                    gridOptions={gridOptions}
                    domLayout="autoHeight"
                    defaultColDef={{ flex: 1, resizable: false }}
                    headerHeight={26}
                    rowData={rowData}
                    pagination
                    paginationPageSize={6}
                    overlayNoRowsTemplate={t('home.budgetStates.empty')}
                    components={{
                        budgetStatesCellRenderer: BudgetStatesCellRenderer,
                    }}
                    columnDefs={columnDefs}
                />
            </StyTableContainer>
        </StyBudgetStatesContainer>
    );
};

export default BudgetStates;
